import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import TxTable from "@/components/TxTable";
import { Card, Pager, usePaged } from "@/components/common";
import { weiToEth } from "@/lib/format";
import { usePageTitle } from "@/lib/seo";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "success", label: "Successful" },
  { id: "failed", label: "Failed" },
  { id: "token_transfer", label: "Token Transfers" },
  { id: "contract_call", label: "Contract Interactions" },
  { id: "swap", label: "Token Swaps" },
  { id: "large", label: "Large (>0.1 ETH)" },
];

export default function TransactionsPage() {
  const [filter, setFilter] = useState("all");
  usePageTitle("Live Transactions", "Live transactions on Robinhood Chain with filters for swaps, token transfers and large transactions.");
  const pager = usePaged((p) => api.get("/transactions", { params: { filter: "validated", ...p } }).then((r) => r.data), []);

  const filtered = useMemo(() => {
    return pager.items.filter((tx) => {
      const types = tx.transaction_types || [];
      switch (filter) {
        case "success": return tx.status === "ok";
        case "failed": return tx.status === "error";
        case "token_transfer": return types.includes("token_transfer");
        case "contract_call": return types.includes("contract_call");
        case "swap": return /swap|multicall|exact/i.test(tx.method || "");
        case "large": return weiToEth(tx.value) > 0.1;
        default: return true;
      }
    });
  }, [pager.items, filter]);

  return (
    <div className="space-y-6 pt-8">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Transactions</h1>
        <p className="text-sm text-muted-foreground mt-1">Live transactions on Robinhood Chain, refreshed as new blocks arrive.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.id} data-testid={`tx-filter-${f.id}`} onClick={() => setFilter(f.id)}
            className={`text-xs rounded-full px-3 py-1.5 border transition-colors duration-150 ${
              filter === f.id ? "bg-primary text-primary-foreground border-primary font-semibold" : "text-muted-foreground hover:text-foreground"
            }`}>
            {f.label}
          </button>
        ))}
        <button data-testid="tx-refresh-btn" onClick={pager.refresh} className="ml-auto text-xs rounded-full px-3 py-1.5 border text-muted-foreground hover:text-foreground transition-colors duration-150">
          ↻ Refresh
        </button>
      </div>

      <Card>
        <TxTable items={filtered} loading={pager.loading} error={pager.error} />
        {!pager.loading && filtered.length > 0 && <Pager pager={pager} />}
        {!pager.loading && !pager.error && pager.items.length > 0 && filtered.length === 0 && (
          <p className="p-6 text-center text-xs text-muted-foreground">No transactions on this page match the filter. Try the next page.</p>
        )}
      </Card>
    </div>
  );
}
